import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  ScrollText,
  Search,
} from "lucide-react";
import { useState } from "react";

import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";

const PAGE_SIZE = 50;

/** What sign-in rows carry in `metadata` (see the server's auth routes). */
interface AuthMetadata {
  method?: string;
  secondFactor?: string;
  provider?: string;
}

const METHOD_LABELS: Record<string, string> = {
  password: "Password",
  passkey: "Passkey",
  social: "Social",
  magic_link: "Magic link",
};

/**
 * How the account authenticated, for rows that record it — sign-ins
 * carry `{ method, secondFactor?, provider? }`. Returns null for the
 * many actions that aren't authentications, so the cell stays quiet
 * rather than showing a dash for every admin action.
 */
const authMethod = (metadata: unknown): string | null => {
  if (typeof metadata !== "object" || metadata === null) return null;
  const m = metadata as AuthMetadata;
  if (typeof m.method !== "string") return null;

  const base = METHOD_LABELS[m.method] ?? m.method;
  if (m.method === "social" && m.provider) {
    return `${base} · ${m.provider}`;
  }
  if (m.secondFactor) {
    // The second factor is the interesting part of a 2FA sign-in.
    const factor = m.secondFactor === "backup_code" ? "backup code" : m.secondFactor;
    return `${base} + ${factor}`;
  }
  return base;
};

/**
 * System-wide access log (superadmin). Server-side filtering by action
 * prefix (e.g. `auth.`, `user.`, `tenant.`) plus offset pagination.
 */
export const AuditLogTable = () => {
  // The input is applied on Enter/blur via `filter` so typing doesn't
  // refetch on every keystroke.
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const { data, error, loading, refresh } = useCall("adminListAuditLogs", {
    actionPrefix: filter || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const applyFilter = () => {
    setOffset(0);
    setFilter(query.trim());
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-[12rem] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onBlur={applyFilter}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilter();
            }}
            placeholder="Filter by action prefix (auth., user., …)"
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCcw />
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    icon={ScrollText}
                    title={filter ? "No matching entries" : "No log entries yet"}
                    description={
                      filter
                        ? "Try a different action prefix."
                        : "Authentication and admin activity will appear here."
                    }
                    className="border-0 rounded-none bg-transparent"
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell
                    title={r.createdAt}
                    className="whitespace-nowrap text-sm text-muted-foreground"
                  >
                    {formatRelative(r.createdAt)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.action}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {authMethod(r.metadata) ?? ""}
                  </TableCell>
                  <TableCell className="max-w-[14rem] truncate text-sm">
                    {r.actorEmail ?? r.actorId ?? "—"}
                  </TableCell>
                  <TableCell className="max-w-[12rem] truncate font-mono text-xs text-muted-foreground">
                    {r.resourceType
                      ? `${r.resourceType}${r.resourceId ? `:${r.resourceId}` : ""}`
                      : "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                    {r.ipAddress ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.success ? "muted" : "destructive"}>
                      {r.success ? "ok" : "failed"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {total > 0
            ? `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`
            : "0 entries"}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            <ChevronLeft />
            Newer
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Older
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
};
